import { snakeize } from 'casing'

/** Segment `labra_auth_provider` payload — aligns with AuthorizationSource mapping. */
export enum LabraAuthProvider {
  Labra = 'labra',
  Hubspot = 'hubspot',
  Salesforce = 'salesforce',
}

/** Product names used in Segment event naming (uppercase). */
export type TrackerProductType =
  | 'OPPSYNC'
  | 'FLYOUT'
  | 'CLOUDFAAS'
  | 'MARKETING'
  | 'SALES'
  | 'INTEGRATIONS'

/** Minimal Segment analytics client surface used by Tracker. */
export type SegmentAnalyticsClient = {
  identify: (...args: unknown[]) => void
  group: (...args: unknown[]) => void
  track: (...args: unknown[]) => void
  page: (...args: unknown[]) => void
}

export type TrackerData = {
  isSidePanelTracking?: boolean
  customTracking?: boolean
  eventName?: string
  product?: TrackerProductType | string
  section?: string
  component?: string
  action?: string
  [key: string]: unknown
}

export class Tracker {
  private segmentClient?: SegmentAnalyticsClient
  private labraAuthProvider?: LabraAuthProvider

  constructor(segmentClient?: SegmentAnalyticsClient) {
    this.segmentClient = segmentClient
  }

  public setLabraAuthProvider = (provider: LabraAuthProvider | undefined) => {
    this.labraAuthProvider = provider
  }

  public identify = (userId: string, context: Record<string, unknown>) => {
    this.segmentClient?.identify(userId, context)
  }

  public group = (groupName: string) => {
    this.segmentClient?.group(groupName)
  }

  /**
   * Track an analytics event following the naming convention:
   * product.page_location.ui_component.action_past_tense
   * e.g. oppsync.aws_cloud_settings.generate_iam_policies_button.clicked
   * more info: https://alexzran.medium.com/naming-convention-how-to-name-tracking-events-605792071591
   *
   * Product lifecycle events name themselves through `eventName` instead, which
   * bypasses the dotted convention — see `useTrackEvent`.
   *
   * @param data event data/properties to track
   */
  public track = (data: TrackerData) => {
    const eventName = data.eventName || this.getEventName(data)

    if (data.isSidePanelTracking || data.customTracking) {
      delete data.product
      delete data.isSidePanelTracking
      delete data.customTracking
    }
    delete data.eventName

    if (this.labraAuthProvider) {
      data.labra_auth_provider = this.labraAuthProvider
    }

    this.segmentClient?.track(eventName, data)
  }

  private getEventName = (data: TrackerData) => {
    const {
      isSidePanelTracking,
      customTracking,
      product,
      section,
      component,
      action,
    } = data
    const sidePanelCustomEventName = 'Side Panel Clicked'
    const eventParts = isSidePanelTracking
      ? [sidePanelCustomEventName]
      : customTracking
        ? [section, component, action]
        : [product, section, component, action]

    return eventParts
      .filter(Boolean)
      .map(str => (str ? str.toLowerCase() : ''))
      .map(snakeize)
      .join('.')
  }

  public page = (properties?: Record<string, unknown>) => {
    const merged: Record<string, unknown> = { ...(properties || {}) }
    if (this.labraAuthProvider) {
      merged.labra_auth_provider = this.labraAuthProvider
    }

    if (Object.keys(merged).length > 0) {
      this.segmentClient?.page(merged as object)
    } else {
      this.segmentClient?.page()
    }
  }
}
